import {
	downloadSSOCertificate,
	downloadSSOMetadata,
	useTenantSSOSettings,
	useUpdateTenantSSOSettings
} from '@api/authMethods';
import { useProjectLimits } from '@api/limits';
import { useProjectSettings } from '@api/settings';
import { apiBaseURL } from '@api/setup';
import { ActionsFooter, ApprovalDialogButton } from '@components/Forms';
import { LimitsExceededDialog } from '@components/GeneralDialogs';
import Loader from '@components/Loader/Loader';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import HelpTooltip from '@components/Tooltips/HelpTooltip';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LoadingButton } from '@mui/lab';
import {
	Alert,
	Box,
	Button,
	Divider,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	Stack,
	Tooltip,
	Typography
} from '@mui/material';
import { onSubmitForm } from '@utils/forms';
import {
	schemaRequiredWhenFieldEqualsValidationScheme,
	stringRequiredWhenFieldEqualsValidationScheme,
	urlValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { ProjectContext } from 'routes/ProjectProvider';
import { LimitType } from 'types/limits';
import { ISSOSettings } from 'types/settings';
import { ITenant } from 'types/tenants';
import { ToastSeverity } from 'types/toast';
import { useBoolean } from 'usehooks-ts';
import { bool, object } from 'yup';
import IdentityProviderSetup from './IdentityProviderSetup';

const FORM_KEYS = {
	idpMetadataURL: 'idpMetadataURL',
	domain: 'domain',
	redirectURL: 'redirectURL',
	samlMappings: 'samlMappings',
	idpURL: 'idpURL',
	entityID: 'entityID',
	descopeEntityID: 'descopeEntityID',
	idpCert: 'idpCert',
	useMetaInfoToggle: 'useMetaInfoToggle'
};

interface ISSODisplaySettings
	extends ISSOSettings,
		Partial<Pick<ITenant, 'domain'>> {}

const DEFAULT_FORM: ISSODisplaySettings = {
	...Object.keys(FORM_KEYS).reduce(
		(prev, current) => ({ ...prev, [current]: '' }),
		{}
	),
	enabled: false
} as ISSODisplaySettings;

interface ISAMLProps {
	tenant?: ITenant;
	showAlert?: boolean;
}

const getXMLURL = (projectId?: string, tenantId?: string): string =>
	`${apiBaseURL()}/v1/auth/saml/metadata?projectId=${projectId}&tenant=${encodeURIComponent(
		tenantId || ''
	)}`;

const SSOSetup: FC<ISAMLProps> = ({ tenant, showAlert }) => {
	const { addToast } = useToast();
	const tenantId = tenant?.id || '';

	const { data, isLoading: isTenantSSOSettingsLoading } =
		useTenantSSOSettings(tenantId);
	const { isLoading: isProjectLoading, data: projectData } =
		useProjectSettings();
	const { value, setFalse, setTrue } = useBoolean();
	const settings = useMemo<ISSODisplaySettings>(() => {
		const pid = tenant?.projectID || '';

		return {
			...DEFAULT_FORM,
			...data,
			domain: tenant?.domain,
			descopeEntityID: data?.descopeEntityID || `${pid}-${tenantId}`,
			XMLURL: getXMLURL(projectData?.projectId, tenantId)
		};
	}, [data, tenant, projectData, tenantId]);
	const mutation = useUpdateTenantSSOSettings(tenantId);
	const downloadMetadata = useCallback(async () => {
		try {
			await downloadSSOMetadata(tenantId);
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('authMethods.SSO.service.provider.download.error'),
				error
			});
		}
	}, [tenantId, addToast]);

	const downloadCertificate = useCallback(async () => {
		try {
			await downloadSSOCertificate(tenantId);
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('authMethods.SSO.service.provider.cert.error'),
				error
			});
		}
	}, [tenantId, addToast]);

	const formik = useFormik<ISSODisplaySettings>({
		initialValues: settings,
		validationSchema: object({
			[FORM_KEYS.redirectURL]: urlValidationScheme(),
			[FORM_KEYS.idpURL]: schemaRequiredWhenFieldEqualsValidationScheme(
				urlValidationScheme(),
				FORM_KEYS.useMetaInfoToggle,
				false
			),
			[FORM_KEYS.entityID]: stringRequiredWhenFieldEqualsValidationScheme(
				FORM_KEYS.useMetaInfoToggle,
				false
			),
			[FORM_KEYS.idpCert]: stringRequiredWhenFieldEqualsValidationScheme(
				FORM_KEYS.useMetaInfoToggle,
				false
			),
			[FORM_KEYS.idpMetadataURL]: schemaRequiredWhenFieldEqualsValidationScheme(
				urlValidationScheme(),
				FORM_KEYS.useMetaInfoToggle,
				true
			),
			[FORM_KEYS.useMetaInfoToggle]: bool()
		}),
		enableReinitialize: true,
		onSubmit: async (values) => {
			if (!values.useMetaInfoToggle) {
				values.idpMetadataURL = '';
			}

			await onSubmitForm<ISSODisplaySettings>(
				async (newData) => {
					await mutation.mutateAsync(newData);
					if (tenant && tenant.domain !== newData.domain) {
						tenant.domain = newData.domain || '';
					}
					return undefined;
				},
				addToast,
				'pages.authMethods.SSO.save.success'
			)({ ...values, enabled: true });
		}
	});

	const { currentProject } = useContext(ProjectContext);
	const { data: limits } = useProjectLimits(currentProject);

	useEffect(() => {
		if (limits?.samlExceeded && data?.enabled === false) {
			setTrue();
		}
	}, [limits, data, setTrue]);

	return (
		<>
			{value && (
				<LimitsExceededDialog limitType={LimitType.SAML} onCancel={setFalse} />
			)}
			<Loader
				isLoading={isTenantSSOSettingsLoading || !tenantId || isProjectLoading}
				variant="settings"
			>
				<Box
					display="flex"
					flexDirection="column"
					flexGrow={1}
					data-testid="authentication-methods-sso"
					component="form"
					overflow="hidden"
					height="100%"
				>
					{showAlert && (
						<Alert severity="warning" sx={{ marginTop: 2, marginBottom: 2 }}>
							{intl.tr('authMethods.SSO.disabled.warning')}
						</Alert>
					)}
					<Box overflow="auto" flexGrow={1} paddingLeft={3}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography mt={3} variant="overline" color="text.secondary">
									{intl.tr('pages.authMethods.SSO.identity.provider.title')}
								</Typography>
								<Typography
									mt={3}
									mb={3}
									variant="body2"
									color="text.primary"
									whiteSpace="break-spaces"
								>
									{intl.tr(
										'pages.authMethods.SSO.identity.provider.description'
									)}
								</Typography>
								<RadioGroup
									name={FORM_KEYS.useMetaInfoToggle}
									value={!!formik.values.useMetaInfoToggle}
								>
									<FormControlLabel
										value="true"
										control={<Radio />}
										checked={!!formik.values.useMetaInfoToggle}
										onChange={() => {
											formik.setFieldValue(FORM_KEYS.useMetaInfoToggle, true);
										}}
										label={intl.tr(
											'pages.authMethods.SSO.identity.provider.url'
										)}
									/>
									{formik.values.useMetaInfoToggle && (
										<Grid container p={3} pl={4}>
											<Grid item xs={8}>
												<TextFieldValidation
													id={FORM_KEYS.idpMetadataURL}
													formik={formik}
													fullWidth
													required
													label={intl.tr(
														'authMethods.SSO.identity.provider.input.XMLMetaData.title'
													)}
												/>
											</Grid>
										</Grid>
									)}
									<FormControlLabel
										value="false"
										checked={!formik.values.useMetaInfoToggle}
										control={<Radio />}
										onChange={() => {
											formik.setFieldValue(FORM_KEYS.useMetaInfoToggle, false);
										}}
										label={intl.tr(
											'pages.authMethods.SSO.identity.provider.manual'
										)}
									/>
									{!formik.values.useMetaInfoToggle && (
										<Grid container p={3} pl={4}>
											<Grid item xs={8}>
												<IdentityProviderSetup formik={formik} />
											</Grid>
										</Grid>
									)}
								</RadioGroup>
							</Grid>
						</Grid>
						<Box display="flex" flexGrow={1} flexDirection="column">
							<Box pt={6} pb={4}>
								<Divider />
							</Box>
							<Typography variant="overline" color="text.secondary">
								{intl.tr('authMethods.SSO.domain.title')}
							</Typography>
							<Box pt={4}>
								<TextFieldValidation
									id={FORM_KEYS.domain}
									formik={formik}
									fullWidth
									label={intl.tr('authMethods.SSO.domain.label')}
								/>
							</Box>
							<Box pt={6} pb={4}>
								<Divider />
							</Box>
							<Typography variant="overline" color="text.secondary">
								{intl.tr('authMethods.SSO.authentication.redirect.title')}
							</Typography>
							<Stack direction="row" spacing={2} mt={3} alignItems="center">
								<Typography
									variant="body2"
									color="text.primary"
									whiteSpace="break-spaces"
								>
									{intl.tr(
										'authMethods.SSO.authentication.redirect.description'
									)}
								</Typography>
								<HelpTooltip
									title={intl.tr(
										'authMethods.SSO.authentication.redirect.description.help'
									)}
									placement="right"
								/>
							</Stack>
							<Box pt={3}>
								<TextFieldValidation
									id={FORM_KEYS.redirectURL}
									formik={formik}
									fullWidth
									label={intl.tr(
										'authMethods.SSO.authentication.redirect.input.label'
									)}
								/>
							</Box>
							<Box pt={6} pb={4}>
								<Divider />
							</Box>
							<Typography variant="overline" color="text.secondary">
								{intl.tr('authMethods.SSO.service.provider.title')}
							</Typography>
							<Typography
								mt={3}
								variant="body2"
								color="text.primary"
								whiteSpace="break-spaces"
							>
								{intl.tr('authMethods.SSO.service.provider.secondary.title')}
							</Typography>
							<Grid container pt={4} pb={4} alignItems="center" spacing={1}>
								<Grid item xs={6}>
									<TextFieldValidation
										id="XMLURL"
										formik={formik}
										copy
										label={intl.tr('authMethods.SSO.service.provider.link')}
										disabled
										fullWidth
									/>
								</Grid>
								<Grid item mr={1} ml={2} textAlign="center">
									<Typography variant="overline" color="text.secondary">
										{intl.tr('lang.or')}
									</Typography>
								</Grid>
								<Grid item xs={4}>
									<Tooltip
										title={intl.tr(
											'authMethods.SSO.service.provider.download.link.disabled'
										)}
										placement="right"
									>
										<span>
											<Button
												color="primary"
												startIcon={<FileDownloadIcon />}
												data-testid="download-metadata-button"
												onClick={downloadMetadata}
											>
												{intl.tr(
													'authMethods.SSO.service.provider.download.link'
												)}
											</Button>
										</span>
									</Tooltip>
								</Grid>
								<Grid item xs={12}>
									<Button
										color="primary"
										data-testid="download-certificate-button"
										startIcon={<FileDownloadIcon />}
										onClick={downloadCertificate}
									>
										{intl.tr('authMethods.SSO.service.provider.cert.link')}
									</Button>
								</Grid>
							</Grid>
							<Grid container pb={4} alignItems="center" spacing={3}>
								<Grid item xs={12}>
									<Grid item xs={6}>
										<TextFieldValidation
											id="descopeEntityID"
											formik={formik}
											fullWidth
											disabled
											label={intl.tr(
												'pages.authMethods.SSO.identity.provider.input.descopeEntityId.title'
											)}
											copy
										/>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<Grid item xs={6}>
										<TextFieldValidation
											id="acsUrl"
											formik={formik}
											value={data?.acsUrl || ''}
											externalValue
											fullWidth
											disabled
											label={intl.tr(
												'pages.authMethods.SSO.identity.provider.acsUrl.title'
											)}
											copy
										/>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					</Box>
					<ActionsFooter>
						<LoadingButton
							color="primary"
							variant="contained"
							loading={formik.isSubmitting}
							disabled={!formik.dirty}
							sx={{ marginRight: 1 }}
							onClick={() => formik.handleSubmit()}
						>
							{intl.tr('button.save')}
						</LoadingButton>
						<ApprovalDialogButton
							buttonText={intl.tr('button.cancel')}
							onApprove={formik.resetForm}
							disabled={!formik.dirty}
							title={intl.tr('discard.changes.dialog.title')}
							question={intl.tr('discard.changes.dialog.question')}
							approveText={intl.tr('discard.changes.dialog.approve')}
						/>
					</ActionsFooter>
				</Box>
			</Loader>
		</>
	);
};

SSOSetup.defaultProps = {
	tenant: undefined,
	showAlert: false
};

export { getXMLURL };
export default SSOSetup;
